import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import axios from 'axios';
import { api, refreshCsrfToken, getErrorMessage } from '@/lib/api/client';
import { useAuthStore } from '@/stores/auth';
import { useUiStore } from '@/stores/ui';
import { router } from '@/router';
import type { User } from '@/types';
import { watch } from 'vue';

/**
 * Navigation helper using Vue Router.
 */
function navigate(path: string): Promise<void> {
    return router.push(path).then(() => undefined);
}

// Helper to get/clear intended URL
function getIntendedUrl(): string | null {
    return sessionStorage.getItem('intended');
}

function clearIntendedUrl(): void {
    sessionStorage.removeItem('intended');
}

/**
 * Auth API response types.
 */
interface AuthResponse {
    data: {
        user: User;
        token: string;
        token_type: string;
    };
}

interface MeResponse {
    data: User;
}

interface TwoFactorRequiredResponse {
    message: string;
    two_factor_required: true;
    challenge_token: string;
}

/**
 * Login mutation input type.
 * Accepts email, username, or mobile number as identifier.
 */
interface LoginInput {
    identifier: string;
    password: string;
    remember?: boolean;
}

/**
 * Register mutation input type.
 */
interface RegisterInput {
    name: string;
    email: string;
    password: string;
    password_confirmation: string;
    invitation_token?: string;
}

/**
 * Two-factor challenge input type.
 */
interface TwoFactorChallengeInput {
    challenge_token: string;
    code: string;
}

/**
 * Query keys for auth-related queries.
 */
export const authKeys = {
    all: ['auth'] as const,
    me: () => [...authKeys.all, 'me'] as const,
};

/**
 * Fetch current authenticated user and sync to auth store.
 *
 * Automatically refetches on window focus and updates the auth store
 * with fresh abilities/roles from the server.
 */
export function useMe() {
    const authStore = useAuthStore();

    const query = useQuery({
        queryKey: authKeys.me(),
        queryFn: async (): Promise<User> => {
            const response = await api.get<MeResponse>('/api/auth/me');
            return response.data.data;
        },
        retry: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: true,
        enabled: authStore.isInitialized,
    });

    // Sync fresh server data to auth store whenever it changes
    watch(() => query.data.value, (userData) => {
        if (userData) {
            authStore.setAuthData({
                user: userData,
                abilities: userData.all_abilities ?? [],
                roles: userData.role_slugs ?? [],
            });
        }
    });

    return query;
}

/**
 * Login mutation.
 */
export function useLogin() {
    const authStore = useAuthStore();
    const uiStore = useUiStore();

    return useMutation({
        mutationFn: async (input: LoginInput) => {
            // Refresh CSRF token before login
            await refreshCsrfToken();

            const response = await api.post<AuthResponse | TwoFactorRequiredResponse>(
                '/api/auth/login',
                input
            );

            return response.data;
        },
        onSuccess: async (data) => {
            // Check if 2FA is required
            if ('two_factor_required' in data && data.two_factor_required) {
                // Store challenge token and redirect to 2FA page
                sessionStorage.setItem('2fa_challenge_token', data.challenge_token);
                await navigate('/two-factor-challenge');
                return;
            }

            // Show loading overlay during transition
            authStore.setLoading(true);

            // Regular login success - use data from response directly
            // The login response includes user with all_abilities and role_slugs
            const authData = data as AuthResponse;
            const user = authData.data.user;

            authStore.setAuthData({
                user,
                abilities: user.all_abilities ?? [],
                roles: user.role_slugs ?? [],
            });

            uiStore.showSuccess('Welcome back!', 'You have successfully logged in.');

            // Redirect to intended URL or dashboard
            const intended = getIntendedUrl();
            clearIntendedUrl();
            await navigate(intended || '/dashboard');
            authStore.setLoading(false);
        },
        onError: (error) => {
            authStore.setLoading(false);

            // Detect account pending response (403 with account_pending flag)
            if (
                axios.isAxiosError(error) &&
                error.response?.status === 403 &&
                error.response?.data?.account_pending
            ) {
                navigate('/account-pending');
                return;
            }

            const message = getErrorMessage(error);
            uiStore.showError('Login Failed', message);
        },
    });
}

/**
 * Register mutation.
 */
export function useRegister() {
    const authStore = useAuthStore();
    const uiStore = useUiStore();

    return useMutation({
        mutationFn: async (input: RegisterInput) => {
            // Refresh CSRF token before register
            await refreshCsrfToken();

            const response = await api.post<AuthResponse>('/api/auth/register', input);
            return response.data;
        },
        onSuccess: async (data) => {
            // Show loading overlay during transition
            authStore.setLoading(true);

            // Use data from response directly
            const user = data.data.user;
            authStore.setAuthData({
                user,
                abilities: user.all_abilities ?? [],
                roles: user.role_slugs ?? [],
            });

            uiStore.showSuccess('Welcome!', 'Your account has been created.');
            await navigate('/dashboard');
            authStore.setLoading(false);
        },
        onError: (error) => {
            authStore.setLoading(false);
            const message = getErrorMessage(error);
            uiStore.showError('Registration Failed', message);
        },
    });
}

/**
 * Logout mutation.
 */
export function useLogout() {
    const queryClient = useQueryClient();
    const authStore = useAuthStore();
    const uiStore = useUiStore();

    return useMutation({
        mutationFn: async (allDevices = false) => {
            await api.post('/api/auth/logout', { all_devices: allDevices });
        },
        onSuccess: async () => {
            authStore.setLoading(true);

            // Clear auth state
            authStore.clearAuth();

            // Clear all queries
            queryClient.clear();

            uiStore.showSuccess('Goodbye!', 'You have been logged out.');
            await navigate('/login');
            authStore.setLoading(false);
        },
        onError: async () => {
            authStore.setLoading(true);

            // Even on error, clear local state
            authStore.clearAuth();
            queryClient.clear();
            await navigate('/login');
            authStore.setLoading(false);
        },
    });
}

/**
 * Two-factor challenge mutation.
 */
export function useTwoFactorChallenge() {
    const authStore = useAuthStore();
    const uiStore = useUiStore();

    return useMutation({
        mutationFn: async (input: TwoFactorChallengeInput) => {
            const response = await api.post<AuthResponse>(
                '/api/auth/two-factor/challenge',
                input
            );
            return response.data;
        },
        onSuccess: async (data) => {
            // Clear challenge token
            sessionStorage.removeItem('2fa_challenge_token');

            // Show loading overlay during transition
            authStore.setLoading(true);

            // Use data from response directly
            const user = data.data.user;
            authStore.setAuthData({
                user,
                abilities: user.all_abilities ?? [],
                roles: user.role_slugs ?? [],
            });

            uiStore.showSuccess('Welcome back!', 'Two-factor authentication successful.');

            // Redirect to intended URL or dashboard
            const intended = getIntendedUrl();
            clearIntendedUrl();
            await navigate(intended || '/dashboard');
            authStore.setLoading(false);
        },
        onError: (error) => {
            authStore.setLoading(false);
            const message = getErrorMessage(error);
            uiStore.showError('Verification Failed', message);
        },
    });
}

/**
 * Refresh token mutation.
 */
export function useRefreshToken() {
    const authStore = useAuthStore();

    return useMutation({
        mutationFn: async () => {
            const response = await api.post<AuthResponse>('/api/auth/refresh-token');
            return response.data;
        },
        onSuccess: (data) => {
            authStore.setUser(data.data.user);
        },
    });
}

/**
 * Forgot password input type.
 */
interface ForgotPasswordInput {
    email: string;
}

/**
 * Forgot password mutation.
 */
export function useForgotPassword() {
    const uiStore = useUiStore();

    return useMutation({
        mutationFn: async (input: ForgotPasswordInput) => {
            await refreshCsrfToken();
            const response = await api.post<{ message: string }>('/api/auth/forgot-password', input);
            return response.data;
        },
        onSuccess: (data) => {
            uiStore.showSuccess('Email Sent', data.message || 'Password reset link has been sent to your email.');
        },
        onError: (error) => {
            const message = getErrorMessage(error);
            uiStore.showError('Request Failed', message);
        },
    });
}

/**
 * Reset password input type.
 */
interface ResetPasswordInput {
    token: string;
    email: string;
    password: string;
    password_confirmation: string;
}

/**
 * Reset password mutation.
 */
export function useResetPassword() {
    const uiStore = useUiStore();

    return useMutation({
        mutationFn: async (input: ResetPasswordInput) => {
            await refreshCsrfToken();
            const response = await api.post<{ message: string }>('/api/auth/reset-password', input);
            return response.data;
        },
        onSuccess: async (data) => {
            uiStore.showSuccess('Password Reset', data.message || 'Your password has been reset.');
            await navigate('/login');
        },
        onError: (error) => {
            const message = getErrorMessage(error);
            uiStore.showError('Reset Failed', message);
        },
    });
}

/**
 * Verify email mutation (resend verification email).
 */
export function useResendVerificationEmail() {
    const uiStore = useUiStore();

    return useMutation({
        mutationFn: async () => {
            const response = await api.post<{ message: string }>('/api/auth/email/verification-notification');
            return response.data;
        },
        onSuccess: (data) => {
            uiStore.showSuccess('Email Sent', data.message || 'Verification link has been sent to your email.');
        },
        onError: (error) => {
            const message = getErrorMessage(error);
            uiStore.showError('Request Failed', message);
        },
    });
}

/**
 * Confirm password input type.
 */
interface ConfirmPasswordInput {
    password: string;
}

/**
 * Confirm password mutation.
 */
export function useConfirmPassword() {
    const uiStore = useUiStore();

    return useMutation({
        mutationFn: async (input: ConfirmPasswordInput) => {
            await refreshCsrfToken();
            const response = await api.post<{ message: string }>('/api/auth/confirm-password', input);
            return response.data;
        },
        onSuccess: async () => {
            // Redirect to intended URL or dashboard after password confirmation
            const intended = getIntendedUrl();
            clearIntendedUrl();
            await navigate(intended || '/dashboard');
        },
        onError: (error) => {
            const message = getErrorMessage(error);
            uiStore.showError('Confirmation Failed', message);
        },
    });
}
